import { create } from 'zustand';
import { favoritesApi } from '../lib/api';
import type { ListEntityKind } from '../types/list';

export const favKey = (kind: ListEntityKind, id: string) => `${kind}:${id}`;

interface FavoriteState {
  keys: Set<string>;
  loaded: boolean;
  hydrate: () => Promise<void>;
  isFavorite: (kind: ListEntityKind, id: string) => boolean;
  toggle: (kind: ListEntityKind, id: string) => Promise<void>;
}

export const useFavoriteStore = create<FavoriteState>((set, get) => ({
  keys: new Set(),
  loaded: false,
  hydrate: async () => {
    const ids = await favoritesApi.ids();
    const keys = new Set<string>();
    (ids.schools || []).forEach((id) => id && keys.add(favKey('SCHOOL', id)));
    (ids.clubTeams || []).forEach((id) => id && keys.add(favKey('CLUB_TEAM', id)));
    (ids.people || []).forEach((id) => id && keys.add(favKey('PERSON', id)));
    (ids.programs || []).forEach((id) => id && keys.add(favKey('PROGRAM', id)));
    set({ keys, loaded: true });
  },
  isFavorite: (kind, id) => get().keys.has(favKey(kind, id)),
  toggle: async (kind, id) => {
    const key = favKey(kind, id);
    const next = new Set(get().keys);
    const wasOn = next.has(key);
    wasOn ? next.delete(key) : next.add(key);
    set({ keys: next });                       // optimistic
    try {
      const { isFavorite } = await favoritesApi.toggle(kind, id);
      const reconciled = new Set(get().keys);
      isFavorite ? reconciled.add(key) : reconciled.delete(key);
      set({ keys: reconciled });
    } catch (e) {
      const rollback = new Set(get().keys);    // revert
      wasOn ? rollback.add(key) : rollback.delete(key);
      set({ keys: rollback });
      throw e;
    }
  },
}));
